import React, {useCallback, useEffect, useState} from 'react';
import {
    validateCardFields,
    validateCheckFields, validateContactInfoAndZeroCheckFields, joinFieldErrors,
} from "./paymentFunctions";
import { useRequestUrl, useCaptchaKey } from "../context/contextHooks";
import { Turnstile } from '@marsidev/react-turnstile';
import { useStyles } from '../styles/styles';
import API from '../http/API';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from "@mui/material/Box";
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Button from "@mui/material/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import CardData from './CardData';
import CheckData from './CheckData';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {getDecimal} from "../global/utils";
import Popup from '../layout/Popup';
import {HtmlComponent} from "../global/components";
import {getFeeQuery} from "./paymentFunctions";
import Verify from "./Verify";

const CompletePayment = (props) => {
    const openDialog = props.openDialog;
    const setOpenDialog = props.setOpenDialog;
    const paymentRequest = props.paymentRequest;
    const setPaymentRequest = props.setPaymentRequest;
    const setPaymentResult = props.setPaymentResult
    const court = props.court;
    const [requestUrl,] = useRequestUrl();
    const [captchaKey,] = useCaptchaKey();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));
    // Phone-width dialogs are too narrow for the full tab labels, which would wrap or be cut off (CPDEV-2771)
    const narrow = useMediaQuery('(max-width:450px)');
    const classes = useStyles;
    const [tabVal, setTabVal] = useState('1');
    // Each request has its own response slot. They used to share one, gated on
    // a single loading flag, so whichever answer arrived second was dropped and
    // a fee, card check or verification setting could silently go missing
    // (CPDEV-2993). An answer to a request that a newer one of the same kind has
    // replaced is ignored, so it cannot overwrite the newer answer.
    const [pending, setPending] = useState(0);
    const loading = pending > 0;
    const latestRequest = React.useRef({});
    const [feeResult, setFeeResult] = useState(null);
    const [cardResult, setCardResult] = useState(null);
    const [verifyResult, setVerifyResult] = useState(null);
    const [authorizeResult, setAuthorizeResult] = useState(null);
    const [paymentFields, setPaymentFields] = useState({paymentType: 'CARD', email: paymentRequest?.email, phone: paymentRequest?.phone, billingZip: paymentRequest?.billingZip});
    const [errorMessage, setErrorMessage] = useState(null);
    // Which controls the current errorMessage is about. Drives per-field
    // aria-invalid and the focus move, neither of which the summary text can
    // express on its own (CPDEV-2777, 3.3.1).
    const [fieldErrors, setFieldErrors] = useState([]);
    const [declineMessage, setDeclineMessage] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [token, setToken] = useState(null);
    const [openNotice, setOpenNotice] = useState(!!court.prePaymentNotice);
    const [prepaymentShown, setPrepaymentShown] = useState(false);
    const captchaRef = React.useRef();
    const [noSubmit, setNoSubmit] = useState(false);
    const [verifyCompleted, setVerifyCompleted] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyRequired, setVerifyRequired] = useState(null);

    const [splitFeeAmts, setSplitFeeAmts] = useState({cardFee: 0, checkFee: 0, totalAmt: 0})

    const getPrePaymentNoticeComponent = () => {
        const title = 'Pre Payment Notice';
        return (
            <HtmlComponent title={title} component={court.prePaymentNotice} />
        );
    }

    // Counts a request in, and returns the callback that counts it out and keeps
    // its answer only if no newer request of the same kind has been sent since.
    const track = (kind, setResult) => {
        const seq = (latestRequest.current[kind] || 0) + 1;
        latestRequest.current[kind] = seq;
        setPending(n => n + 1);
        return (response) => {
            setPending(n => n - 1);
            if (latestRequest.current[kind] === seq) setResult(response);
        };
    }

    const calculateFee = (paymentType) => {
        setErrorMessage(null);
        const courtAmt = paymentRequest.courtAmount;
        const url = `${requestUrl}/calculateFee`;
        API.postAction(url, getFeeQuery(court, courtAmt, paymentType), 'calculateFee', null, track('calculateFee', setFeeResult));
    }

    const checkCardType = (cardNo, expMo, expYr) => {
        setErrorMessage(null);
        const request = {courtId: court.courtId, cardNumber: cardNo, expMonth: expMo, expYear: expYr};
        const url = `${requestUrl}/checkCard`;
        API.postAction(url, request, 'checkCard', null, track('checkCard', setCardResult));
    }

    const getVerifyRequired = () => {
        const url = `${requestUrl}/getVerifyRequired/${court.courtId}`;
        API.getRetrieve(url,'getVerifyRequired', null, track('getVerifyRequired', setVerifyResult));
    }

    const handleFeeResponse = (response) => {
        const fee = response.citepayFee;
        const total = response.totalAmount;
        setPaymentRequest({...paymentRequest, feeAmount: fee, totalAmount: total});
        if (court.onlineCheckFeeConfig && (court.checkFeeForDebit || !court.noChex) && tabVal === '1') {
            setSplitFeeAmts({cardFee: response.onlineCardFee, checkFee: response.onlineCheckFee, totalAmt: total});
        }
    }

    const handleCardResponse = (response) => {
        let feeAmt;
        if (splitFeeAmts) {
            if (court.checkFeeForDebit) {
                if ((response.checkCard || response.debitCard) && splitFeeAmts.checkFee) {
                    feeAmt = splitFeeAmts.checkFee;
                } else if (splitFeeAmts.cardFee) {
                    feeAmt = splitFeeAmts.cardFee;
                }
            } else feeAmt = splitFeeAmts.cardFee;
            if (feeAmt) {
                const totalAmt = parseFloat(getDecimal(paymentRequest.courtAmount + feeAmt, false));
                setPaymentRequest({...paymentRequest, feeAmount: feeAmt, totalAmount: totalAmt});
            }
        }
    }

    const handleVerificationReturned = (verifyFields) => {
        const request = {...paymentRequest, ...paymentFields, ...verifyFields};
        setVerifyOpen(false);
        sendPaymentRequest(request);
        setVerifyCompleted(false);
        setPaymentRequest(request);
    }

    const sendPaymentRequest = (request) => {
        const url = `${requestUrl}/authorizePayment`;
        const payload = JSON.stringify(request);
        API.postAction(url, payload, 'authorizePayment', '', track('authorizePayment', setAuthorizeResult));
    }

    const handleTabChange = (e, newValue) => {
        setPaymentFields({...paymentFields, paymentType: (newValue === '2' ? 'CHECK' : 'CARD')});
        calculateFee(newValue === '2' ? 'CHECK' : 'CARD');
        setTabVal(newValue);
    }

    const Widget = useCallback( () => {
        if (!captchaKey) return;
        return (
            <Turnstile
                ref={captchaRef}
                siteKey={captchaKey}
                options={{
                    theme: 'light',
                    size: `${small ? 'compact' : 'normal'}`
                }}
                onSuccess={(token) => setToken(token)}
                onExpire={() => setToken(null)}
            />
        );
    }, [captchaKey, small]);

    const reportErrors = (errors) => {
        setFieldErrors(errors);
        setErrorMessage(joinFieldErrors(errors));
    }

    const handleSubmit = () => {
        if (noSubmit) return;
        setNoSubmit(true);
        let sendRequest = false;
        setErrorMessage(null);
        setFieldErrors([]);
        // Validate the same merged view that will be sent. A prefilled field the
        // user never touched only exists on paymentRequest; one they typed into or
        // cleared only exists on paymentFields. Validating paymentFields alone
        // would call a prefilled name blank, and validating paymentRequest alone
        // would miss one the user had just erased (CPDEV-2777).
        const fields = {...paymentRequest, ...paymentFields};
        let errors = fields.paymentType === 'CHECK'
            ? validateCheckFields(fields, court)
            : validateCardFields(fields, court);
        if (errors.length > 0) {
            reportErrors(errors);
        } else if (!token) {
            setErrorMessage("Please complete the Captcha box");
        } else {
            paymentRequest.totalAmount = parseFloat(getDecimal(paymentRequest.totalAmount, false));
            paymentRequest.paymentOperator = 'CitePay user';
            paymentRequest.captchaToken = token;
            const request = {...paymentRequest, ...paymentFields};
            errors = validateContactInfoAndZeroCheckFields(request, court);
            if (errors.length > 0) {
                reportErrors(errors);
            } else if (verifyRequired && !verifyCompleted) {
                setVerifyOpen(true);
            } else {
                sendRequest = true;
                sendPaymentRequest(request);
                setToken(null);
            }
        }
        if (!sendRequest) setNoSubmit(false);
    }

    // 3.3.1/2.4.3: put the user on the first field at fault rather than leaving
    // focus on Submit, where the summary Alert is above and out of reach. The
    // field also points aria-describedby at the Alert while it is the one in
    // error, so the description is read out on arrival.
    useEffect(() => {
        const target = fieldErrors.find((e) => e.field);
        if (!target) return;
        // Not every id belongs to something focusable: the state Select puts the
        // id on its root and the real <select> carries a different one, so fall
        // back to the focusable descendant, then to the shared name attribute.
        const holder = document.getElementById(target.field);
        const focusable = holder && (holder.matches('input, select, textarea')
            ? holder
            : holder.querySelector('input, select, textarea'));
        (focusable || document.querySelector(`[name="${target.field}"]`))?.focus();
    }, [fieldErrors]);

    const reportFailure = (response) => {
        setErrorMessage(response.errorMessage);
        captchaRef.current?.reset();
    }

    useEffect(() => {
        if (!feeResult) return;
        if (feeResult.error) reportFailure(feeResult);
        else handleFeeResponse(feeResult);
    }, [feeResult])

    // Also re-run when the split fees arrive, so a card checked before they did
    // is still priced for its type. A card answer landing after a switch to the
    // Check tab must not put a card fee on a check payment.
    useEffect(() => {
        if (!cardResult || tabVal !== '1') return;
        if (cardResult.error) reportFailure(cardResult);
        else handleCardResponse(cardResult);
    }, [cardResult, splitFeeAmts, tabVal])

    useEffect(() => {
        if (!verifyResult) return;
        if (verifyResult.error) reportFailure(verifyResult);
        else setVerifyRequired(verifyResult.success);
    }, [verifyResult])

    useEffect(() => {
        if (!authorizeResult) return;
        if (authorizeResult.error) {
            reportFailure(authorizeResult);
        } else if (!authorizeResult.authorized) {
            setDeclineMessage(authorizeResult.declineMessage);
            captchaRef.current?.reset();
        } else {
            setPaymentResult(authorizeResult);
            setOpenDialog(false);
        }
        setNoSubmit(false);
    }, [authorizeResult])

    useEffect(() => {
        if (court.prePaymentNotice && prepaymentShown) {
            setOpenNotice(false);
        }
    }, [prepaymentShown])

    useEffect(() => {
        setAcceptTerms(false);
        setVerifyCompleted(false);
        setTabVal('1');
        if (openDialog && !prepaymentShown) setOpenNotice(!!court.prePaymentNotice);
        if (openDialog) getVerifyRequired();
    }, [openDialog])

    useEffect(() => {
        if (court && court.onlineCheckFeeConfig && (!court.noChex || court.checkFeeForDebit) && tabVal === '1' && !splitFeeAmts.cardFee > 0) {
            calculateFee('CARD');
        }
    }, [court, tabVal, splitFeeAmts]);

    return (
        <>
            { openNotice &&
                <Popup
                    open={openNotice}
                    setDone={setPrepaymentShown}
                    title="Payment Notice"
                    component={getPrePaymentNoticeComponent()}
                />
            }
            { captchaKey && !openNotice &&
                <Dialog
                    open={openDialog}
                    maxWidth='sm'
                    fullWidth
                    sx={classes.dialog}
                    slotProps={{
                        paper: {sx: classes.listContainer}
                    }}
                >
                    <Backdrop sx={classes.backdrop} open={loading}>
                        <CircularProgress color="inherit"/>
                    </Backdrop>
                    <DialogTitle sx={{ ...classes.title, fontWeight: 700 }} component="h2">Enter Payment Information</DialogTitle>
                    { errorMessage &&
                        <Alert id='payment-error' severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            <AlertTitle>Error!</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    { declineMessage &&
                        <Alert severity='warning' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            {declineMessage}
                        </Alert>
                    }
                    { verifyOpen &&
                        <Verify
                            open={verifyOpen}
                            setOpen={setVerifyOpen}
                            paymentRequest={paymentRequest}
                            paymentFields={paymentFields}
                            court={court}
                            handleVerificationReturned={handleVerificationReturned}
                        />
                    }
                    <DialogContent dividers sx={{...classes.listContainer, minHeight: '60vh', padding: small ? '0' : ''}} >
                        <Box sx={{ width: '100%', typography: 'body1'}}>
                            {court.noChex ?
                                <CardData
                                    showTitle={true}
                                    paymentRequest={paymentRequest}
                                    paymentFields={paymentFields}
                                    setPaymentFields={setPaymentFields}
                                    setAcceptTerms={setAcceptTerms}
                                    checkCardType={checkCardType}
                                    splitFees={!!(court.onlineCheckFeeConfig && court.checkFeeForDebit)}
                                    fieldErrors={fieldErrors}
                                />:
                                <TabContext value={tabVal}>
                                    <Box sx={{...classes.title, borderBottom: 1, borderColor: 'divider', marginBottom: '0', paddingBottom: '0' }}>
                                        <TabList onChange={handleTabChange} variant='fullWidth' aria-label='Payment method'>
                                            <Tab label={narrow ? 'Card' : 'Card Payment'} aria-label='Card Payment' value='1' />
                                            <Tab label={narrow ? 'Check' : 'Check Payment'} aria-label='Check Payment' value='2'/>
                                        </TabList>
                                    </Box>
                                    <TabPanel value='1'>
                                        <CardData
                                            showTitle={false}
                                            paymentRequest={paymentRequest}
                                            paymentFields={paymentFields}
                                            setPaymentFields={setPaymentFields}
                                            setAcceptTerms={setAcceptTerms}
                                            checkCardType={checkCardType}
                                            splitFees={court.onlineCheckFeeConfig}
                                            fieldErrors={fieldErrors}
                                        />
                                    </TabPanel>
                                    <TabPanel value='2'>
                                        <CheckData
                                            paymentRequest={paymentRequest}
                                            paymentFields={paymentFields}
                                            setPaymentFields={setPaymentFields}
                                            setAcceptTerms={setAcceptTerms}
                                            splitFees={court.onlineCheckFeeConfig}
                                            fieldErrors={fieldErrors}
                                        />
                                    </TabPanel>
                                </TabContext>
                            }
                        </Box>
                        <div style={{margin: '.5rem 0', display: 'flex', justifyContent: 'center'}}>
                            <div>
                                <Widget />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{paddingTop: '0', justifyContent: small ? 'center' : 'flex'}}>
                        {/* DOM order is the visual order (2.4.3); justifyContent keeps the
                            row right-aligned, which is what row-reverse used to do. */}
                        <Box component='div' sx={{display: 'flex', justifyContent: 'flex-end', margin: '1rem', padding: '0'}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={tiny ? null : <ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={() => {
                                    setErrorMessage(null);
                                    setDeclineMessage(null);
                                    setPrepaymentShown(false);
                                    setOpenDialog(false);
                                    // Drop the request so the next Pay mounts a fresh
                                    // CompletePayment. Kept, it held the first cart's card
                                    // fee and priced the next cart with it (CPDEV-2993).
                                    setPaymentRequest(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={tiny ? null : <SendTwoToneIcon />}
                                disabled={!acceptTerms || !token || noSubmit}
                                sx={classes.squashedButton}
                                onClick={(event) => {
                                    event.target.disabled = true;
                                    handleSubmit();
                                    event.target.disabled = (!acceptTerms || !token || noSubmit)
                                }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
}

export default CompletePayment;
